import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IconChevronRight, IconBrandStrava } from '@tabler/icons-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { getChallengeTotalDistanceMeters } from '../utils/challengeProgress';
import {
  readCachedStravaActivities,
  writeCachedStravaActivities,
} from '../utils/stravaActivitiesCache';
import GpxMapPage from '../pages/GpxMapPage';
import './ChallengeAhaHero.css';

// The native in-map checkpoint popup shows a checkpoint within this range of the
// puck stop (mirrors GpxMapPage's RANGE), so the headline names the same one.
const POPUP_RANGE_METERS = 5000;
// If Strava is slow, resolve with whatever we have (cache or fallback) so the
// hero does not hang on a spinner.
const RESOLVE_TIMEOUT_MS = 4000;

// Pure geometry, intentionally local: the hero only needs distances for the
// headline. Mirrors GpxMapPage's calculateCumulativeDistances /
// findCheckpointDistanceOnRoute without coupling to that 5000-line module.
const haversineMeters = (lng1, lat1, lng2, lat2) => {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const buildCumulativeDistances = (coordinates) => {
  const distances = [0];
  let total = 0;
  for (let i = 1; i < coordinates.length; i++) {
    const [lng1, lat1] = coordinates[i - 1];
    const [lng2, lat2] = coordinates[i];
    total += haversineMeters(lng1, lat1, lng2, lat2);
    distances.push(total);
  }
  return distances;
};

const checkpointDistanceOnRoute = (coordinates, cumulative, checkpoint) => {
  if (!checkpoint || typeof checkpoint.lon !== 'number' || typeof checkpoint.lat !== 'number') {
    return null;
  }
  let bestIdx = 0;
  let bestDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < coordinates.length; i++) {
    const [lng, lat] = coordinates[i];
    const dist = haversineMeters(lng, lat, checkpoint.lon, checkpoint.lat);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }
  return cumulative[bestIdx];
};

// Reconstruct [lng, lat] pairs. The raw gpxMapConfigs doc stores flattened
// lngs/lats; coordinates is rebuilt inside GpxMapPage, so it is usually absent
// on the route handed to the hero.
const getCoordinates = (route) => {
  if (Array.isArray(route?.coordinates) && route.coordinates.length > 1) {
    return route.coordinates;
  }
  if (Array.isArray(route?.lngs) && Array.isArray(route?.lats) && route.lngs.length > 1) {
    return route.lngs.map((lng, i) => [lng, route.lats[i]]);
  }
  return [];
};

const formatKm = (meters) => (meters / 1000).toFixed(1);

const verbForActivityType = (type) => {
  const t = String(type || '').toLowerCase();
  if (t.includes('run')) return 'run';
  if (t.includes('walk')) return 'walk';
  if (t.includes('hike')) return 'hike';
  if (t.includes('ride') || t.includes('cycl') || t.includes('bike')) return 'ride';
  return 'effort';
};

const ChallengeAhaHero = ({ user, challenge, route, posthog }) => {
  // Frozen, first-resolved projection input. activity is { distanceMeters, type }
  // or null (no usable activity). resolvedRef freezes it so a late fresh fetch
  // cannot re-drive a flyover that already played.
  const [activity, setActivity] = useState(null);
  const [projectionResolved, setProjectionResolved] = useState(false);
  const [connected, setConnected] = useState(true);
  const resolvedRef = useRef(false);

  const frameRef = useRef(null);
  const [inView, setInView] = useState(false);

  // Resolve the rider's most recent activity distance once, preferring the
  // instant localStorage cache, then a fresh fetch, then a timeout fallback.
  useEffect(() => {
    resolvedRef.current = false;
    setProjectionResolved(false);
    setActivity(null);
    setConnected(true);

    if (!user?.uid) {
      resolvedRef.current = true;
      setProjectionResolved(true);
      return undefined;
    }

    let cancelled = false;
    const resolve = (act) => {
      if (resolvedRef.current || cancelled) return;
      resolvedRef.current = true;
      if (act !== undefined) setActivity(act);
      setProjectionResolved(true);
    };
    const toActivity = (raw) => {
      if (raw && Number.isFinite(raw.distance) && raw.distance > 0) {
        return { distanceMeters: raw.distance, type: raw.type };
      }
      return null;
    };

    // Instant value from cache (the /create page is the canonical writer).
    const cached = readCachedStravaActivities(user.uid);
    const cachedActivity = toActivity(cached?.activities?.[0]);
    if (cachedActivity) {
      resolve(cachedActivity);
    }

    const timeout = setTimeout(() => resolve(null), RESOLVE_TIMEOUT_MS);

    const run = async () => {
      try {
        const res = await httpsCallable(functions, 'getStravaActivities')();
        if (cancelled) return;
        const activities = Array.isArray(res?.data) ? res.data : [];
        writeCachedStravaActivities(user.uid, activities);
        resolve(toActivity(activities[0]));
      } catch (err) {
        if (cancelled) return;
        let needsReconnect = false;
        if (err?.message) {
          try { needsReconnect = JSON.parse(err.message)?.needsReconnect === true; } catch (_) { /* not JSON */ }
        }
        if (needsReconnect || err?.code === 'permission-denied' || err?.code === 'unauthenticated') {
          setConnected(false);
        }
        resolve(null);
      } finally {
        if (!cancelled) clearTimeout(timeout);
      }
    };
    run();

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [user?.uid]);

  // Lazy-mount the heavy viewer only when the hero nears the viewport.
  useEffect(() => {
    const el = frameRef.current;
    if (!el || inView) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setInView(true);
        observer.disconnect();
      }
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView]);

  // Project the activity onto the route: the distance to feed the embed and the
  // adaptive headline copy.
  const projection = useMemo(() => {
    const title = challenge?.title || 'this journey';
    const coordinates = getCoordinates(route);
    const totalMeters = getChallengeTotalDistanceMeters(route);
    const checkpoints = Array.isArray(route?.checkpoints) ? route.checkpoints : [];

    if (coordinates.length < 2 || !totalMeters) {
      return { distanceMeters: 0, headline: `See how far your runs would carry you on ${title}.`, mode: 'fallback' };
    }

    const cumulative = buildCumulativeDistances(coordinates);
    const cpDistances = checkpoints.map((cp) => checkpointDistanceOnRoute(coordinates, cumulative, cp));
    const firstCp = cpDistances.find((d) => typeof d === 'number' && d > 100) || 0;
    const finalName = (checkpoints.find((c) => c?.isEnd)?.name)
      || (checkpoints.length ? checkpoints[checkpoints.length - 1]?.name : null)
      || 'the finish';

    // No usable activity: clean flyover to the first checkpoint + generic copy.
    if (!activity) {
      return {
        distanceMeters: firstCp,
        headline: `See how far your last run would carry you on ${title}.`,
        mode: 'fallback',
      };
    }

    const projected = Math.max(0, Math.min(activity.distanceMeters, totalMeters));
    const km = formatKm(projected);
    const verb = verbForActivityType(activity.type);

    // Finishes the whole route.
    if (projected >= totalMeters * 0.999) {
      return {
        distanceMeters: projected,
        headline: `Your last ${km} km ${verb} would carry you all the way to ${finalName}.`,
        mode: 'finish',
      };
    }

    // Nearest checkpoint to the stop (skip the start marker) within popup range.
    let nearest = null;
    let nearestDiff = Infinity;
    let lastPassed = null;
    cpDistances.forEach((d, i) => {
      if (typeof d !== 'number' || d <= 100) return;
      if (d <= projected + 1) lastPassed = checkpoints[i];
      const diff = Math.abs(d - projected);
      if (diff < nearestDiff) {
        nearestDiff = diff;
        nearest = checkpoints[i];
      }
    });

    if (nearest && nearestDiff <= POPUP_RANGE_METERS) {
      return {
        distanceMeters: projected,
        headline: `Your last ${km} km ${verb} would already carry you to ${nearest.name}.`,
        mode: 'reached',
      };
    }

    if (lastPassed) {
      return {
        distanceMeters: projected,
        headline: `Your last ${km} km ${verb} would carry you past ${lastPassed.name} on ${title}.`,
        mode: 'midleg',
      };
    }

    const firstCpName = checkpoints.find((cp, i) => typeof cpDistances[i] === 'number' && cpDistances[i] > 100)?.name;
    return {
      distanceMeters: projected,
      headline: firstCpName
        ? `Your last ${km} km ${verb} would already put ${firstCpName} within reach on ${title}.`
        : `Your last ${km} km ${verb} would already get you moving on ${title}.`,
      mode: 'opening',
    };
  }, [challenge, route, activity]);

  if (!challenge || !route) return null;

  const shouldMountMap = inView && projectionResolved;
  const showConnectNudge = projectionResolved && projection.mode === 'fallback' && !connected;

  const handlePickAdventure = () => {
    posthog?.capture('challenge_aha_pick_adventure_clicked', {
      page: 'challenge_landing',
      challenge_id: challenge.id,
      projection_mode: projection.mode,
    });
    document.getElementById('seven-wonders')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="section-dark challenge-aha-hero">
      <div className="challenge-aha-hero-inner">
        <span className="featured-eyebrow">Your next adventure</span>
        <h2 className="challenge-aha-hero-headline">{projection.headline}</h2>

        <div className="challenge-aha-hero-frame" ref={frameRef}>
          {shouldMountMap ? (
            <GpxMapPage
              embedConfigId={challenge.id}
              viewerOnly
              autoplay
              projectedDistanceMeters={projection.distanceMeters}
              user={null}
            />
          ) : (
            <div className="challenge-aha-hero-loading">
              <div className="challenge-aha-hero-spinner" />
            </div>
          )}
        </div>

        {showConnectNudge && (
          <p className="challenge-aha-hero-nudge">
            <IconBrandStrava size={16} />
            Connect Strava to see how far your own runs would carry you here.
          </p>
        )}

        <button type="button" className="challenge-aha-hero-cta" onClick={handlePickAdventure}>
          Pick your adventure
          <IconChevronRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default ChallengeAhaHero;
